import { useRef } from 'react'

import { useLightMode } from '@/hooks/useLightMode'
import { useTranslation } from 'react-i18next'
import { useDebouncedCallback } from 'use-debounce'

import { AnimatePresence, motion } from 'framer-motion'

import { Box, Flex, Spinner, Text } from '@hivewatch/dark'

import { Notification } from '@/graphql/appsync-notifications'

import { getNotificationsTabTokens } from '../../styles/tokens'
import { InboxListItem } from './InboxListItem'

export const InboxList = ({
  notifications,
  isLoading,
  onEndReached,
  onEndReachedThreshold = 0.95,
}: {
  notifications?: Notification[]
  isLoading: boolean
  onEndReached: () => void
  onEndReachedThreshold?: number
}) => {
  const { t } = useTranslation()
  const colorScheme = useLightMode()
  const tokens = getNotificationsTabTokens(colorScheme)
  const ref = useRef()

  const debouncedOnEndReached = useDebouncedCallback(onEndReached, 500)

  const handleScroll = ({ currentTarget }) => {
    const scrollPosition = currentTarget.scrollTop + currentTarget.clientHeight
    if (scrollPosition >= currentTarget.scrollHeight * onEndReachedThreshold) {
      debouncedOnEndReached()
    }
  }

  if (isLoading) {
    return <InboxSpinner colorScheme={colorScheme} />
  }

  return (
    <Box
      bg={tokens.surface}
      data-testid='InboxList:wrapper'
      h='full'
      onScroll={handleScroll}
      overflowY='scroll'
      ref={ref}
    >
      <Box pos='relative'>
        {notifications?.length > 0 ? (
          notifications?.map((n) => (
            <InboxListItem key={n?.notificationTimestamp} notification={n} />
          ))
        ) : (
          <Text
            color={tokens.textSecondary}
            py={3}
            textAlign='center'
            textStyle='body-bold'
          >
            {t('common:notification_inbox_no_messages')}
          </Text>
        )}
      </Box>
    </Box>
  )
}

const InboxSpinner = ({ colorScheme }: { colorScheme: 'light' | 'dark' }) => {
  const tokens = getNotificationsTabTokens(colorScheme)
  return (
    <Flex
      alignItems='center'
      data-testid='InboxList:wrapper'
      height='100%'
      justifyContent='center'
      width='full'
    >
      <AnimatePresence>
        <motion.div
          animate={{ opacity: 1 }}
          data-testid='InboxSpinner'
          exit={{ opacity: 0 }}
          initial={{ opacity: 0 }}
        >
          <Spinner borderWidth='3px' color={tokens.textSecondary} size='md' />
        </motion.div>
      </AnimatePresence>
    </Flex>
  )
}
